import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

/**
 * The preview card for a link in a chat bubble — title, description, image
 * and site, as the page publishes them (GET /api/link-preview).
 *
 * Answers are kept for the session in a module-level cache, shared by every
 * bubble, so scrolling a long thread or re-opening a chat doesn't ask again —
 * including "no preview", which draws nothing.
 */
const cache = new Map();   // url -> preview | null | Promise

const loadPreview = (url) => {
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    const pending = api.get('/link-preview', { params: { url } })
        .then((res) => res.data?.preview || null)
        .catch(() => null)
        .then((preview) => {
            cache.set(url, preview);
            return preview;
        });
    cache.set(url, pending);
    return pending;
};

const LinkPreviewCard = ({ url }) => {
    const cached = cache.get(url);
    const [preview, setPreview] = useState(cached instanceof Promise ? undefined : cached);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        let active = true;
        setImageFailed(false);
        loadPreview(url).then((p) => { if (active) setPreview(p); });
        return () => { active = false; };
    }, [url]);

    if (!preview) return null;

    return (
        <a
            className="msgr-linkcard"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
        >
            {preview.image && !imageFailed && (
                <img
                    className="msgr-linkcard-image"
                    src={preview.image}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setImageFailed(true)}
                />
            )}
            <span className="msgr-linkcard-body">
                {preview.siteName && <span className="msgr-linkcard-site">{preview.siteName}</span>}
                {preview.title && <span className="msgr-linkcard-title">{preview.title}</span>}
                {preview.description && <span className="msgr-linkcard-desc">{preview.description}</span>}
            </span>
        </a>
    );
};

export default LinkPreviewCard;
